import { defineComponent as _defineComponent } from 'vue';
import { resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "grid grid-cols-[auto_1fr] min-h-full bg-(--colors-background-white)" };
const _hoisted_2 = {
    class: "min-w-0 px-5 pt-6 pb-4 bg-(--colors-background-white)",
    "data-testid": "donation-module-settings-view"
};
const _hoisted_3 = { class: "flex flex-col gap-5" };
const _hoisted_4 = { class: "flex items-center justify-between gap-4" };
const _hoisted_5 = { class: "text-2xl sm:text-3xl font-bold text-(--colors-text-heading)" };
const _hoisted_6 = {
    class: "sr-only",
    "aria-live": "polite",
    "data-testid": "donation-module-settings-save-status"
};
const _hoisted_7 = { class: "max-w-full overflow-x-auto" };
const _hoisted_8 = { class: "inline-flex items-center gap-1.5 whitespace-nowrap" };
const _hoisted_9 = ["data-testid"];
const _hoisted_10 = { class: "sr-only" };
const _hoisted_11 = ["id", "aria-labelledby", "data-testid"];
import { Breadcrumb, Button, ButtonGroup, buttonGroupTabId, FormSaveButton, UnsavedChangesModal } from "@eudonet/opal";
import { computed, nextTick, onMounted, ref, shallowRef, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { moduleRegistry } from "@/router/moduleRegistry";
import ModuleLoading from "@/shared/components/ModuleLoading.vue";
import ModuleState from "@/shared/components/ModuleState.vue";
import { useAppToast } from "@/shared/composables/useAppToast";
import { useWorkspaceLegalInfoForm } from "@/modules/workspace/public";
import { useTaxReceiptEmailContextsForm } from "../../composables/useTaxReceiptEmailContextsForm";
import { TAX_RECEIPT_RECIPIENTS_PICKER_ENABLED, useTaxReceiptNotificationRecipients, } from "../../composables/useTaxReceiptNotificationRecipients";
import { useTaxReceiptSettingsForm } from "../../composables/useTaxReceiptSettingsForm";
import { useTaxReceiptTemplateOptions } from "../../composables/useTaxReceiptTemplateOptions";
import { isTaxReceiptEmailSubjectInvalid, TAX_RECEIPT_EMAIL_CONTEXTS, } from "../../domain/entities/taxReceiptEmailContexts";
import { DONATION_MODULE_SETTINGS_DEFAULT_TAB, DONATION_MODULE_SETTINGS_TABS, isDonationModuleSettingsTab, } from "../composables/donationModuleSettingsTabs";
import DonationSettingsActivationTab from "../components/donation-module-settings/DonationSettingsActivationTab.vue";
import DonationSettingsAutoEmissionTab from "../components/donation-module-settings/DonationSettingsAutoEmissionTab.vue";
import DonationSettingsDocumentTemplatesTab from "../components/donation-module-settings/DonationSettingsDocumentTemplatesTab.vue";
import DonationSettingsEmailTemplatesTab from "../components/donation-module-settings/DonationSettingsEmailTemplatesTab.vue";
import DonationSettingsErrorSummary from "../components/donation-module-settings/DonationSettingsErrorSummary.vue";
import DonationSettingsIdentityTab from "../components/donation-module-settings/DonationSettingsIdentityTab.vue";
import DonationSettingsNumberingTab from "../components/donation-module-settings/DonationSettingsNumberingTab.vue";
import DonationSettingsTriggerTab from "../components/donation-module-settings/DonationSettingsTriggerTab.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DonationModuleSettingsView',
    setup(__props) {
        const SidebarComponent = shallowRef(moduleRegistry.getComponentForSlot("sidebar"));
        const { t } = useI18n();
        const { toastSuccess, toastError } = useAppToast();
        const route = useRoute();
        const router = useRouter();
        const form = useTaxReceiptSettingsForm();
        const recipients = useTaxReceiptNotificationRecipients();
        const legalInfo = useWorkspaceLegalInfoForm();
        const templates = useTaxReceiptTemplateOptions();
        const emailContexts = useTaxReceiptEmailContextsForm();
        const breadcrumb = computed(() => [t("settings.title"), t("module.title"), t("module.finance"), t("finance.donationModuleSettings.title")].map((label) => ({ label })));
        const documentTemplateOptions = computed(() => templates.withSelected(templates.documentTemplates.value, [
            form.state.documentTemplateIndividualId,
            form.state.documentTemplateOrganizationId,
        ]));
        const emailTemplateOptions = computed(() => templates.withSelected(templates.emailTemplates.value, TAX_RECEIPT_EMAIL_CONTEXTS.map((context) => emailContexts.state[context].defaultTemplateId)));
        const recipientOptions = computed(() => [
            ...recipients.options.value,
            ...recipients.optionsForSelected(form.state.recipients),
        ]);
        // ── Tabs ────────────────────────────────────────────────────────────────────
        // The active tab lives in `?tab=`, so a reload or a shared link lands on it. With the module off
        // the tab bar holds Activation alone, whatever the query says.
        const panelId = useId();
        const isTabLocked = (key) => !form.state.moduleEnabled && key !== DONATION_MODULE_SETTINGS_DEFAULT_TAB;
        const visibleTabs = computed(() => DONATION_MODULE_SETTINGS_TABS.filter((key) => !isTabLocked(key)));
        const requestedTab = computed(() => isDonationModuleSettingsTab(route.query.tab) ? route.query.tab : DONATION_MODULE_SETTINGS_DEFAULT_TAB);
        const activeTab = computed(() => isTabLocked(requestedTab.value) ? DONATION_MODULE_SETTINGS_DEFAULT_TAB : requestedTab.value);
        async function selectTab(key) {
            if (isTabLocked(key) || route.query.tab === key)
                return;
            await router.replace({ query: { ...route.query, tab: key } });
        }
        const tabPanel = ref(null);
        // A summary link always lands in the panel, including the tab the blocked save already opened.
        async function openTabFromSummary(key) {
            if (!isDonationModuleSettingsTab(key))
                return;
            await selectTab(key);
            await nextTick();
            tabPanel.value?.focus();
        }
        // With the module off the query is pointed back at Activation for good: a query still naming a
        // hidden tab would reopen it the moment the box is ticked again. Run on every switch-off and after
        // each load (a stored "off" does not change the form's default, so the watch alone misses it).
        function pointQueryAtActivationWhileOff() {
            if (form.state.moduleEnabled)
                return;
            if (route.query.tab === undefined || route.query.tab === DONATION_MODULE_SETTINGS_DEFAULT_TAB)
                return;
            void router.replace({ query: { ...route.query, tab: DONATION_MODULE_SETTINGS_DEFAULT_TAB } });
        }
        watch(() => form.state.moduleEnabled, (enabled) => {
            pointQueryAtActivationWhileOff();
            // A refusal made with the module on must not come back when it is ticked again without a
            // new save.
            if (!enabled)
                clearSaveRefusal();
        });
        // ── Required fields across tabs ─────────────────────────────────────────────
        // Set by a save refused for a missing or invalid field, client-side or by the server, cleared by
        // the next save that passes the client checks and when the module is unticked. The red dots
        // follow the live state: fixing a tab's last error removes its dot.
        const saveBlocked = ref(false);
        // CONSTRAINT: the summary is a role="alert" live region, so it lists the tabs of the refused save
        // and stays as is until the next save; a live list would be announced again at every fix.
        const summaryTabs = ref([]);
        function clearSaveRefusal() {
            saveBlocked.value = false;
            summaryTabs.value = [];
        }
        // A template catalogue that failed to load holds nothing back: nothing can be chosen in it. An
        // email context whose catalogue failed is waived only while untouched, since its select still
        // offers "none".
        const documentTemplatesMissing = computed(() => !templates.documentsLoadError.value && !form.hasDocumentTemplates.value);
        const missingEmailContexts = computed(() => emailContexts.loadError.value
            ? []
            : TAX_RECEIPT_EMAIL_CONTEXTS.filter((context) => emailContexts.state[context].defaultTemplateId === null &&
                !(templates.emailTemplatesLoadError.value && !emailContexts.isContextDirty(context))));
        // OPDON refuses the whole context PUT on a bad subject, module on or off: checked before any writer
        // runs, or the settings and legal info would land without it.
        const invalidSubjectContexts = computed(() => emailContexts.loadError.value
            ? []
            : TAX_RECEIPT_EMAIL_CONTEXTS.filter((context) => (form.state.moduleEnabled || emailContexts.isContextDirty(context)) &&
                isTaxReceiptEmailSubjectInvalid(emailContexts.state[context].subject)));
        const invalidEmailContexts = computed(() => TAX_RECEIPT_EMAIL_CONTEXTS.filter((context) => missingEmailContexts.value.includes(context) || invalidSubjectContexts.value.includes(context)));
        function tabHasError(key) {
            const settingsErrors = form.errors.value;
            switch (key) {
                case "identity":
                    // `form` is OPUS refusing the read-only identity: it is fixed on the workspace page, and
                    // only the next legal-info save clears it, so it never blocks nor points here.
                    return Object.keys(legalInfo.errors.value).some((errorKey) => errorKey !== "form");
                case "numbering":
                    return Boolean(settingsErrors.numberingMode || settingsErrors.numberingPrefix || settingsErrors.numberingCounterSize);
                case "trigger":
                    return Boolean(settingsErrors.triggerDate || settingsErrors.campaignFirstFiscalYear || settingsErrors.recipients);
                case "documentTemplates":
                    return documentTemplatesMissing.value;
                case "emailTemplates":
                    return invalidEmailContexts.value.length > 0;
                default:
                    return false;
            }
        }
        const invalidTabs = computed(() => saveBlocked.value && form.state.moduleEnabled ? DONATION_MODULE_SETTINGS_TABS.filter(tabHasError) : []);
        const tabLabel = (key) => t(`finance.donationModuleSettings.tabs.${key}`);
        const errorSummaryTabs = computed(() => form.state.moduleEnabled ? summaryTabs.value.map((key) => ({ key, label: tabLabel(key) })) : []);
        const errorSummary = ref(null);
        // ── Save ────────────────────────────────────────────────────────────────────
        // Three writers behind one Save: the OPDON settings row (document templates included), the OPUS
        // legal info (signatory, receipt object, qualification, signature) and the OPDON email contexts
        // (one PUT per changed context). Each is written only when it changed, but with the module on
        // every tab must be complete, visited or not.
        const isDirty = computed(() => form.isDirty.value || legalInfo.isDirty.value || emailContexts.isDirty.value);
        const isSaving = computed(() => form.isSaving.value || legalInfo.isSaving.value || emailContexts.isSaving.value);
        // A block still loading reads as empty and would be reported missing.
        const isLoadingBlocks = computed(() => legalInfo.isLoading.value || emailContexts.isLoading.value);
        const savedOnce = ref(false);
        const saveStatus = computed(() => {
            if (isSaving.value)
                return t("finance.taxReceipts.settings.savingStatus");
            if (savedOnce.value && !isDirty.value)
                return t("finance.taxReceipts.settings.savedStatus");
            return "";
        });
        async function load() {
            await form.load();
            if (form.loadError.value)
                return;
            pointQueryAtActivationWhileOff();
            // The other blocks and the recipients picker degrade on their own: a failure there keeps the
            // settings form usable, so neither shares the page-level error state.
            await Promise.all([
                TAX_RECEIPT_RECIPIENTS_PICKER_ENABLED ? recipients.load(form.selectedUserIds.value) : Promise.resolve(),
                legalInfo.load(),
                templates.load(),
                emailContexts.load(),
            ]);
        }
        // Set with the module off too: the dots and the summary then show once it is ticked again.
        async function showRefusedTabs(refusedTabs) {
            saveBlocked.value = true;
            summaryTabs.value = refusedTabs;
            const firstInvalid = refusedTabs[0];
            if (!form.state.moduleEnabled || !firstInvalid)
                return;
            await selectTab(firstInvalid);
            await nextTick();
            errorSummary.value?.focus();
        }
        async function save() {
            const moduleEnabled = form.state.moduleEnabled;
            const saveSettings = form.isDirty.value;
            const saveLegalInfo = legalInfo.isDirty.value;
            const saveEmailContexts = emailContexts.isDirty.value;
            // Module on: every tab counts, written or not. Module off: nothing is required, but a part that
            // is written must still be valid — the OPDON PUT carries every field, greyed tabs included. A
            // blank legal-info field is not invalid there: OPUS stores it, only issuing receipts needs it.
            const settingsInvalid = (moduleEnabled || saveSettings) && !form.isValid.value;
            const legalInfoInvalid = moduleEnabled
                ? !legalInfo.loadError.value && !legalInfo.isValid.value
                : saveLegalInfo && !legalInfo.isValidIncomplete.value;
            const legalInfoSaveOptions = { requireComplete: moduleEnabled };
            const requiredMissing = moduleEnabled && (documentTemplatesMissing.value || missingEmailContexts.value.length > 0);
            const emailSubjectsInvalid = invalidSubjectContexts.value.length > 0;
            if (settingsInvalid || legalInfoInvalid || requiredMissing || emailSubjectsInvalid) {
                // save() on an invalid part only fills its inline errors; nothing is written.
                if (settingsInvalid)
                    await form.save();
                if (legalInfoInvalid)
                    await legalInfo.save(legalInfoSaveOptions);
                const refusedTabs = DONATION_MODULE_SETTINGS_TABS.filter(tabHasError);
                if (!moduleEnabled) {
                    toastError("finance.taxReceipts.settings.errors.hiddenBlocksInvalid");
                }
                else if (refusedTabs.length === 0) {
                    // CONSTRAINT: every error key the client validators can set must map to a tab in tabHasError;
                    // a refusal no tab claims gets this generic toast instead of the summary.
                    toastError("workspace.settings.errors.formInvalid");
                }
                await showRefusedTabs(refusedTabs);
                return;
            }
            // Every tab passed the client checks: a write the server refuses must not leave the earlier
            // refusal's tabs listed. Emptying the list is not announced again.
            clearSaveRefusal();
            let failed = false;
            if (saveSettings && (await form.save()) !== "saved") {
                failed = true;
                toastError(form.saveErrorKey.value ?? "finance.taxReceipts.settings.errors.saveFailed");
            }
            const legalInfoFailed = saveLegalInfo && (await legalInfo.save(legalInfoSaveOptions)) !== "saved";
            // CONSTRAINT: only the legal-info writer maps a server refusal to a field (OPUS
            // `FIELD_ERROR_CODES`), shown inline on Identity. Never filter every tab here: with the module
            // off, unset templates pass the client checks and would be listed as refused by any failure.
            const identityRefused = legalInfoFailed && tabHasError("identity");
            if (legalInfoFailed) {
                failed = true;
                // The focused summary announces a field refusal, like a client one; a second alert-role toast
                // would be read out too. With the module off the summary waits for the re-tick.
                if (!identityRefused)
                    toastError(legalInfo.saveErrorKey.value ?? "workspace.settings.saveError");
                else if (!moduleEnabled)
                    toastError("finance.taxReceipts.settings.errors.hiddenBlocksInvalid");
            }
            if (saveEmailContexts && (await emailContexts.save()) !== "saved") {
                failed = true;
                toastError(emailContexts.saveErrorKey.value ?? "finance.taxReceipts.settings.errors.emailContextsSaveFailed");
            }
            if (failed) {
                if (identityRefused)
                    await showRefusedTabs(["identity"]);
            }
            else {
                savedOnce.value = true;
                toastSuccess("finance.taxReceipts.settings.saveSuccess");
            }
        }
        // ── Unsaved-changes guard (route leave; a tab switch only changes the query) ─
        const showUnsavedChangesModal = ref(false);
        const pendingNavigation = ref(null);
        onBeforeRouteLeave((_to, _from, next) => {
            if (!isDirty.value) {
                next();
                return;
            }
            pendingNavigation.value = { proceed: () => next(), cancel: () => next(false) };
            showUnsavedChangesModal.value = true;
        });
        function handleLeave() {
            showUnsavedChangesModal.value = false;
            const navigation = pendingNavigation.value;
            pendingNavigation.value = null;
            form.reset();
            legalInfo.reset();
            emailContexts.reset();
            navigation?.proceed();
        }
        function handleStay() {
            showUnsavedChangesModal.value = false;
            const navigation = pendingNavigation.value;
            pendingNavigation.value = null;
            navigation?.cancel();
        }
        onMounted(() => {
            void load();
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (SidebarComponent.value)
                    ? (_openBlock(), _createBlock(_resolveDynamicComponent(SidebarComponent.value), { key: 0 }))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("div", _hoisted_2, [
                    _createElementVNode("div", _hoisted_3, [
                        _createVNode(_unref(Breadcrumb), {
                            items: breadcrumb.value,
                            ariaLabel: _ctx.$t('finance.donationModuleSettings.breadcrumbLabel')
                        }, null, 8 /* PROPS */, ["items", "ariaLabel"]),
                        _createElementVNode("div", _hoisted_4, [
                            _createElementVNode("h1", _hoisted_5, _toDisplayString(_ctx.$t("finance.donationModuleSettings.title")), 1 /* TEXT */),
                            (!_unref(form).isLoading.value && !_unref(form).loadError.value)
                                ? (_openBlock(), _createBlock(_unref(FormSaveButton), {
                                    key: 0,
                                    enabled: isDirty.value && !isLoadingBlocks.value,
                                    saving: isSaving.value,
                                    "save-label": _ctx.$t('form.saveChanges'),
                                    "saving-label": _ctx.$t('form.saving'),
                                    "disabled-hint": _ctx.$t('form.saveDisabledHint'),
                                    "data-testid": "donation-module-settings-save",
                                    onSave: save
                                }, null, 8 /* PROPS */, ["enabled", "saving", "save-label", "saving-label", "disabled-hint"]))
                                : _createCommentVNode("v-if", true)
                        ]),
                        _createElementVNode("p", _hoisted_6, _toDisplayString(saveStatus.value), 1 /* TEXT */),
                        (_unref(form).isLoading.value)
                            ? (_openBlock(), _createBlock(ModuleLoading, {
                                key: 0,
                                "data-testid": "donation-module-settings-loading"
                            }))
                            : (_unref(form).loadError.value)
                                ? (_openBlock(), _createBlock(ModuleState, {
                                    key: 1,
                                    type: "error",
                                    "data-testid": "donation-module-settings-load-error",
                                    title: _ctx.$t('finance.taxReceipts.settings.loadError'),
                                    "cta-icon": "ri-refresh-line",
                                    "cta-label": _ctx.$t('finance.taxReceipts.settings.retry'),
                                    onCta: load
                                }, null, 8 /* PROPS */, ["title", "cta-label"]))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                    (errorSummaryTabs.value.length > 0)
                                        ? (_openBlock(), _createBlock(DonationSettingsErrorSummary, {
                                            key: 0,
                                            ref_key: "errorSummary",
                                            ref: errorSummary,
                                            tabs: errorSummaryTabs.value,
                                            onSelect: openTabFromSummary
                                        }, null, 8 /* PROPS */, ["tabs"]))
                                        : _createCommentVNode("v-if", true),
                                    _createElementVNode("div", _hoisted_7, [
                                        _createVNode(_unref(ButtonGroup), {
                                            size: "xs",
                                            "model-value": activeTab.value,
                                            "panel-id": _unref(panelId),
                                            "aria-label": _ctx.$t('finance.donationModuleSettings.tabsLabel'),
                                            "data-testid": "donation-module-settings-tabs",
                                            "overflow-label": _ctx.$t('common.tabs.next'),
                                            "overflow-previous-label": _ctx.$t('common.tabs.previous'),
                                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (selectTab($event)))
                                        }, {
                                            default: _withCtx(() => [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleTabs.value, (key) => {
                                                    return (_openBlock(), _createBlock(_unref(Button), {
                                                        key: key,
                                                        variant: "tertiary",
                                                        size: "xs",
                                                        value: key,
                                                        "data-testid": `donation-module-settings-tab-${key}`
                                                    }, {
                                                        default: _withCtx(() => [
                                                            _createElementVNode("span", _hoisted_8, [
                                                                _createTextVNode(_toDisplayString(tabLabel(key)) + " ", 1 /* TEXT */),
                                                                (invalidTabs.value.includes(key))
                                                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                                        _createElementVNode("span", {
                                                                            class: "size-2 shrink-0 rounded-full bg-(--components-badge-background-badge-error-strong-bg)",
                                                                            "data-testid": `donation-module-settings-tab-${key}-invalid`,
                                                                            "aria-hidden": "true"
                                                                        }, null, 8 /* PROPS */, _hoisted_9),
                                                                        _createElementVNode("span", _hoisted_10, _toDisplayString(`, ${_ctx.$t("finance.donationModuleSettings.tabs.invalidLabel")}`), 1 /* TEXT */)
                                                                    ], 64 /* STABLE_FRAGMENT */))
                                                                    : _createCommentVNode("v-if", true)
                                                            ])
                                                        ]),
                                                        _: 2 /* DYNAMIC */
                                                    }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["value", "data-testid"]));
                                                }), 128 /* KEYED_FRAGMENT */))
                                            ]),
                                            _: 1 /* STABLE */
                                        }, 8 /* PROPS */, ["model-value", "panel-id", "aria-label", "overflow-label", "overflow-previous-label"])
                                    ]),
                                    _createElementVNode("div", {
                                        id: _unref(panelId),
                                        ref_key: "tabPanel",
                                        ref: tabPanel,
                                        role: "tabpanel",
                                        "aria-labelledby": _unref(buttonGroupTabId)(_unref(panelId), activeTab.value),
                                        tabindex: "0",
                                        class: "flex flex-col gap-5",
                                        "data-testid": `donation-module-settings-panel-${activeTab.value}`
                                    }, [
                                        (activeTab.value === 'activation')
                                            ? (_openBlock(), _createBlock(DonationSettingsActivationTab, {
                                                key: 0,
                                                "module-enabled": _unref(form).state.moduleEnabled,
                                                "onUpdate:moduleEnabled": _cache[1] || (_cache[1] = ($event) => (_unref(form).setField('moduleEnabled', $event)))
                                            }, null, 8 /* PROPS */, ["module-enabled"]))
                                            : (activeTab.value === 'identity')
                                                ? (_openBlock(), _createBlock(DonationSettingsIdentityTab, {
                                                    key: 1,
                                                    settings: _unref(legalInfo).settings,
                                                    errors: _unref(legalInfo).errors.value,
                                                    "is-loading": _unref(legalInfo).isLoading.value,
                                                    "load-error": _unref(legalInfo).loadError.value,
                                                    onRetry: _unref(legalInfo).load,
                                                    "onUpdate:field": _unref(legalInfo).setField,
                                                    "onSignature:upload": _unref(legalInfo).onSignatureUpload,
                                                    "onSignature:remove": _unref(legalInfo).onSignatureRemove,
                                                    "onSignature:cancel": _unref(legalInfo).onSignatureCancel
                                                }, null, 8 /* PROPS */, ["settings", "errors", "is-loading", "load-error", "onRetry", "onUpdate:field", "onSignature:upload", "onSignature:remove", "onSignature:cancel"]))
                                                : (activeTab.value === 'numbering')
                                                    ? (_openBlock(), _createBlock(DonationSettingsNumberingTab, {
                                                        key: 2,
                                                        "numbering-mode": _unref(form).state.numberingMode,
                                                        "numbering-prefix": _unref(form).state.numberingPrefix,
                                                        "numbering-counter-size": _unref(form).state.numberingCounterSize,
                                                        "numbering-locked": _unref(form).state.numberingLocked,
                                                        "number-preview": _unref(form).numberPreview.value,
                                                        "numbering-mode-error": _unref(form).errors.value.numberingMode,
                                                        "numbering-prefix-error": _unref(form).errors.value.numberingPrefix,
                                                        "numbering-counter-size-error": _unref(form).errors.value.numberingCounterSize,
                                                        "onUpdate:numberingMode": _cache[2] || (_cache[2] = ($event) => (_unref(form).setField('numberingMode', $event))),
                                                        "onUpdate:numberingPrefix": _cache[3] || (_cache[3] = ($event) => (_unref(form).setField('numberingPrefix', $event))),
                                                        "onUpdate:numberingCounterSize": _cache[4] || (_cache[4] = ($event) => (_unref(form).setField('numberingCounterSize', $event)))
                                                    }, null, 8 /* PROPS */, ["numbering-mode", "numbering-prefix", "numbering-counter-size", "numbering-locked", "number-preview", "numbering-mode-error", "numbering-prefix-error", "numbering-counter-size-error"]))
                                                    : (activeTab.value === 'trigger')
                                                        ? (_openBlock(), _createBlock(DonationSettingsTriggerTab, {
                                                            key: 3,
                                                            "campaign-trigger-day": _unref(form).state.campaignTriggerDay,
                                                            "campaign-trigger-month": _unref(form).state.campaignTriggerMonth,
                                                            "campaign-first-fiscal-year": _unref(form).state.campaignFirstFiscalYear,
                                                            recipients: _unref(form).state.recipients,
                                                            "recipient-options": recipientOptions.value,
                                                            "recipients-loading": _unref(recipients).isLoading.value,
                                                            "recipients-searching": _unref(recipients).isSearching.value,
                                                            "recipients-load-error": _unref(recipients).loadError.value,
                                                            "trigger-date-error": _unref(form).errors.value.triggerDate,
                                                            "campaign-first-fiscal-year-error": _unref(form).errors.value.campaignFirstFiscalYear,
                                                            "recipients-error": _unref(form).errors.value.recipients,
                                                            "onUpdate:campaignTriggerDay": _cache[5] || (_cache[5] = ($event) => (_unref(form).setField('campaignTriggerDay', $event))),
                                                            "onUpdate:campaignTriggerMonth": _cache[6] || (_cache[6] = ($event) => (_unref(form).setField('campaignTriggerMonth', $event))),
                                                            "onUpdate:campaignFirstFiscalYear": _cache[7] || (_cache[7] = ($event) => (_unref(form).setField('campaignFirstFiscalYear', $event))),
                                                            "onUpdate:recipients": _cache[8] || (_cache[8] = ($event) => (_unref(form).setField('recipients', $event))),
                                                            onSearch: _unref(recipients).search
                                                        }, null, 8 /* PROPS */, ["campaign-trigger-day", "campaign-trigger-month", "campaign-first-fiscal-year", "recipients", "recipient-options", "recipients-loading", "recipients-searching", "recipients-load-error", "trigger-date-error", "campaign-first-fiscal-year-error", "recipients-error", "onSearch"]))
                                                        : (activeTab.value === 'autoEmission')
                                                            ? (_openBlock(), _createBlock(DonationSettingsAutoEmissionTab, {
                                                                key: 4,
                                                                mode: _unref(form).state.mode,
                                                                "onUpdate:mode": _cache[9] || (_cache[9] = ($event) => (_unref(form).setField('mode', $event)))
                                                            }, null, 8 /* PROPS */, ["mode"]))
                                                            : (activeTab.value === 'documentTemplates')
                                                                ? (_openBlock(), _createBlock(DonationSettingsDocumentTemplatesTab, {
                                                                    key: 5,
                                                                    "individual-template-id": _unref(form).state.documentTemplateIndividualId,
                                                                    "organization-template-id": _unref(form).state.documentTemplateOrganizationId,
                                                                    options: documentTemplateOptions.value,
                                                                    "load-error": _unref(templates).documentsLoadError.value,
                                                                    "show-required-errors": saveBlocked.value,
                                                                    "onUpdate:individualTemplateId": _cache[10] || (_cache[10] = ($event) => (_unref(form).setField('documentTemplateIndividualId', $event))),
                                                                    "onUpdate:organizationTemplateId": _cache[11] || (_cache[11] = ($event) => (_unref(form).setField('documentTemplateOrganizationId', $event)))
                                                                }, null, 8 /* PROPS */, ["individual-template-id", "organization-template-id", "options", "load-error", "show-required-errors"]))
                                                                : (_openBlock(), _createBlock(DonationSettingsEmailTemplatesTab, {
                                                                    key: 6,
                                                                    contexts: _unref(emailContexts).state,
                                                                    "template-options": emailTemplateOptions.value,
                                                                    "is-loading": _unref(emailContexts).isLoading.value,
                                                                    "load-error": _unref(emailContexts).loadError.value,
                                                                    "templates-load-error": _unref(templates).emailTemplatesLoadError.value,
                                                                    "invalid-contexts": saveBlocked.value ? invalidEmailContexts.value : [],
                                                                    onRetry: _unref(emailContexts).load,
                                                                    "onUpdate:defaultTemplate": _unref(emailContexts).setDefaultTemplate,
                                                                    "onUpdate:subject": _unref(emailContexts).setSubject
                                                                }, null, 8 /* PROPS */, ["contexts", "template-options", "is-loading", "load-error", "templates-load-error", "invalid-contexts", "onRetry", "onUpdate:defaultTemplate", "onUpdate:subject"]))
                                    ], 8 /* PROPS */, _hoisted_11)
                                ], 64 /* STABLE_FRAGMENT */)),
                        _createVNode(_unref(UnsavedChangesModal), {
                            modelValue: showUnsavedChangesModal.value,
                            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event) => ((showUnsavedChangesModal).value = $event)),
                            title: _ctx.$t('unsavedChanges.title'),
                            message: _ctx.$t('unsavedChanges.message'),
                            "stay-label": _ctx.$t('unsavedChanges.stay'),
                            "leave-label": _ctx.$t('unsavedChanges.leave'),
                            onStay: handleStay,
                            onLeave: handleLeave
                        }, null, 8 /* PROPS */, ["modelValue", "title", "message", "stay-label", "leave-label"])
                    ])
                ])
            ]));
        };
    }
});
